
/**
 * Interface representing the minimal state object required for syncing tool settings.
 * This allows the shared package to remain independent of the core package.
 */
export interface SyncableState {
  current_tool?: { id: string };
  pen_width: number;
  pen_opacity: number;
  brush_hardness: number;
  brush_flow: number;
  spray_radius: number;
  spray_density: number;
  fill_tolerance: number;
  [key: string]: any;
}

/**
 * Interface representing all persistable tool settings.
 */
export interface ToolSettings {
  pen: { size: number; opacity: number; };
  brush: { size: number; opacity: number; hardness: number; };
  eraser: { size: number; opacity: number; hardness: number; };
  spray: { radius: number; density: number; opacity: number; };
  fill: { tolerance: number; opacity: number; };
}

/**
 * Default values for tool settings.
 */
export const DEFAULT_TOOL_SETTINGS: ToolSettings = {
  pen: { size: 2, opacity: 100 },
  brush: { size: 10, opacity: 100, hardness: 80 },
  eraser: { size: 15, opacity: 100, hardness: 50 },
  spray: { radius: 20, density: 30, opacity: 100 },
  fill: { tolerance: 32, opacity: 100 },
};

const STORAGE_KEY = 'hcie-tool-settings-v2';

let globalSettings: ToolSettings | null = null;

/**
 * Loads tool settings from localStorage, merging with defaults.
 * @param state Optional state object to sync loaded settings into.
 */
export function loadToolSettings(state?: SyncableState): ToolSettings {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) {
    globalSettings = JSON.parse(JSON.stringify(DEFAULT_TOOL_SETTINGS));
  } else {
    try {
      const parsed = JSON.parse(stored);
      // Deep merge with defaults to handle versioning/new keys
      globalSettings = {
        pen: { ...DEFAULT_TOOL_SETTINGS.pen, ...parsed.pen },
        brush: { ...DEFAULT_TOOL_SETTINGS.brush, ...parsed.brush },
        eraser: { ...DEFAULT_TOOL_SETTINGS.eraser, ...parsed.eraser },
        spray: { ...DEFAULT_TOOL_SETTINGS.spray, ...parsed.spray },
        fill: { ...DEFAULT_TOOL_SETTINGS.fill, ...parsed.fill },
      };
    } catch (e) {
      console.error('[SETTINGS] Failed to parse tool settings, using defaults', e);
      globalSettings = JSON.parse(JSON.stringify(DEFAULT_TOOL_SETTINGS));
    }
  }
  
  // Apply to provided state if applicable
  if (state) {
    syncToState(state);
  }
  
  // Dispatch event for UI components to refresh
  window.dispatchEvent(new CustomEvent('settingsRestored'));
  
  return globalSettings!;
}

/**
 * Saves current tool settings to localStorage.
 * @param settings Optional partial settings to update before saving.
 * @param state Optional state object to sync from before saving.
 */
export function saveToolSettings(settings?: Partial<ToolSettings>, state?: SyncableState) {
  if (!globalSettings) {
    loadToolSettings();
  }
  
  if (settings) {
    // Deep update
    Object.keys(settings).forEach((key) => {
      const toolKey = key as keyof ToolSettings;
      if (globalSettings && settings[toolKey]) {
        globalSettings[toolKey] = { ...globalSettings[toolKey], ...settings[toolKey] } as any;
      }
    });
  } else if (state) {
    // Sync FROM state before saving to ensure we have manual UI changes
    syncFromState(state);
  }

  localStorage.setItem(STORAGE_KEY, JSON.stringify(globalSettings));
  
  // Ensure state is up to date after merge/save if provided
  if (state) {
    syncToState(state);
  }
  
  console.log('[SETTINGS] Tool settings saved');
}

/**
 * Returns the current in-memory settings singleton.
 */
export function getGlobalSettings(): ToolSettings {
  if (!globalSettings) {
    return loadToolSettings();
  }
  return globalSettings;
}

/**
 * Syncs the structured globalSettings to a flat state object.
 */
export function syncToState(state: SyncableState) {
  if (!globalSettings || !state) return;
  
  const toolId = state.current_tool?.id;
  if (toolId) {
    restoreSettingsForTool(toolId, state);
  } else {
    // Fallback: sync pen defaults
    state.pen_width = globalSettings.pen.size;
    state.pen_opacity = globalSettings.pen.opacity / 100;
  }
}

/**
 * Restores tool-specific settings from the global settings object to a state object.
 * @param toolId The ID of the tool (e.g., 'btn-pen').
 * @param state The state object to update.
 */
export function restoreSettingsForTool(toolId: string, state: SyncableState) {
  if (!globalSettings) loadToolSettings();
  
  console.log(`[SETTINGS] Restoring settings for ${toolId}`);
  
  if (toolId === 'btn-pen') {
    state.pen_width = globalSettings!.pen.size;
    state.pen_opacity = globalSettings!.pen.opacity / 100;
  } else if (toolId === 'btn-brush') {
    state.pen_width = globalSettings!.brush.size;
    state.brush_hardness = globalSettings!.brush.hardness / 100;
    state.pen_opacity = globalSettings!.brush.opacity / 100;
  } else if (toolId === 'btn-eraser') {
    state.pen_width = globalSettings!.eraser.size;
    state.brush_hardness = globalSettings!.eraser.hardness / 100;
    state.pen_opacity = globalSettings!.eraser.opacity / 100;
  } else if (toolId === 'btn-spray') {
    state.spray_radius = globalSettings!.spray.radius;
    state.spray_density = globalSettings!.spray.density;
    state.pen_opacity = globalSettings!.spray.opacity / 100;
  } else if (toolId === 'btn-flood-fill') {
    state.fill_tolerance = globalSettings!.fill.tolerance;
    state.pen_opacity = globalSettings!.fill.opacity / 100;
  }
}

/**
 * Syncs from flat state object back to structured globalSettings.
 */
export function syncFromState(state: SyncableState) {
  if (!globalSettings || !state) return;
  
  const toolId = state.current_tool?.id;
  
  if (toolId === 'btn-pen') {
    globalSettings.pen.size = state.pen_width;
    globalSettings.pen.opacity = Math.round(state.pen_opacity * 100);
  } else if (toolId === 'btn-brush') {
    globalSettings.brush.size = state.pen_width; 
    globalSettings.brush.hardness = Math.round(state.brush_hardness * 100);
    globalSettings.brush.opacity = Math.round(state.pen_opacity * 100);
  } else if (toolId === 'btn-eraser') {
    globalSettings.eraser.size = state.pen_width;
    globalSettings.eraser.hardness = Math.round(state.brush_hardness * 100);
    globalSettings.eraser.opacity = Math.round(state.pen_opacity * 100);
  } else if (toolId === 'btn-spray') {
    globalSettings.spray.radius = state.spray_radius;
    globalSettings.spray.density = state.spray_density;
    globalSettings.spray.opacity = Math.round(state.pen_opacity * 100);
  } else if (toolId === 'btn-flood-fill') {
    globalSettings.fill.tolerance = state.fill_tolerance;
    globalSettings.fill.opacity = Math.round(state.pen_opacity * 100);
  }
}
